import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Typography,
  Chip,
  useTheme,
  LinearProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getAllEntriesWithDetails } from "../features/entry/entrySlice";
import UploadCsvDialog from "../entries/uploadCsvDialog";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import moment from "moment";

export default function AdminEntries() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const entries = useSelector((state) => state.entry.entries);
  const isLoading = useSelector((state) => state.entry.isLoading);

  useEffect(() => {
    dispatch(getAllEntriesWithDetails());
  }, [dispatch]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Ensure entries is an array
  const entriesArray = Array.isArray(entries) ? entries : [];

  // Sort entries by created_at descending
  const sortedEntries = [...entriesArray].sort(
    (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
  );

  const paginatedEntries = sortedEntries.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  if (isLoading && entries.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3 }}>
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">
          All Entries ({sortedEntries.length})
        </Typography>
        <Button
          variant="contained"
          color="success"
          startIcon={<CloudUploadIcon />}
          onClick={() => setUploadDialogOpen(true)}
          disabled={isLoading}
        >
          Import CSV
        </Button>
      </Box>

      <TableContainer component={Paper}>
        {isLoading && entriesArray.length > 0 && (
          <Box sx={{ position: "absolute", width: "100%", top: 0, left: 0 }}>
            <LinearProgress />
          </Box>
        )}
        <Table>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? theme.palette.grey[800]
                    : theme.palette.grey[200],
              }}
            >
              <TableCell
                sx={{ fontWeight: "bold", color: theme.palette.text.primary }}
              >
                Player
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", color: theme.palette.text.primary }}
              >
                Event
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", color: theme.palette.text.primary }}
              >
                Event Date
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", color: theme.palette.text.primary }}
              >
                Category
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", color: theme.palette.text.primary }}
              >
                Position
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", color: theme.palette.text.primary }}
              >
                Category Type
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", color: theme.palette.text.primary }}
              >
                Points
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", color: theme.palette.text.primary }}
              >
                District
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", color: theme.palette.text.primary }}
              >
                Status
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", color: theme.palette.text.primary }}
              >
                Created
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedEntries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                  <Typography color="textSecondary">
                    No entries found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedEntries.map((entry) => (
                <TableRow key={entry.id} hover>
                  <TableCell>
                    {entry.first_name} {entry.last_name}{" "}
                    <span style={{ fontWeight: "bold" }}>
                      [{entry.bsa_number || "-"}]
                    </span>
                  </TableCell>
                  <TableCell>{entry.event}</TableCell>
                  <TableCell>
                    {entry.event_date
                      ? moment(entry.event_date).format("YYYY-MM-DD")
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {entry.category_name || entry.category || "-"}
                  </TableCell>
                  <TableCell align="center">
                    {entry.position_name || entry.position || "-"}
                  </TableCell>
                  <TableCell>
                    {entry.category_type || entry.categoryType || "-"}
                  </TableCell>
                  <TableCell align="right">
                    {(() => {
                      let tooltip = undefined;
                      if (entry.points_description) {
                        try {
                          const desc =
                            typeof entry.points_description === "string"
                              ? JSON.parse(entry.points_description)
                              : entry.points_description;
                          if (
                            desc &&
                            desc.breakdown &&
                            Array.isArray(desc.breakdown)
                          ) {
                            tooltip =
                              `Base Points: ${desc.basePoints}\n` +
                              desc.breakdown
                                .map(
                                  (b) =>
                                    `${b.step}: ${b.points} (Allocation: ${b.allocation}%)`,
                                )
                                .join("\n") +
                              `\nTotal Points: ${desc.totalPoints}`;
                          } else {
                            tooltip = JSON.stringify(desc, null, 2);
                          }
                        } catch (e) {}
                      }
                      return (
                        <span title={tooltip}>
                          <Chip
                            label={entry.points}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </span>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {entry.district || entry.district_id || "-"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={entry.is_valid ? "Valid" : "Invalid"}
                      size="small"
                      color={entry.is_valid ? "success" : "error"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem" }}>
                    {entry.created_at
                      ? moment(entry.created_at).format("YYYY-MM-DD HH:mm")
                      : "-"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={sortedEntries.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <UploadCsvDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onSuccess={() => {
          setUploadDialogOpen(false);
          // Refresh entries list with details
          dispatch(getAllEntriesWithDetails());
        }}
      />
    </Box>
  );
}
